import {
  Dispatch,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';

import {
  Accordion,
  ChakraFlex,
  ChakraFormControl,
  ChakraSimpleGrid,
  FAIconName,
  FireflyButton,
  Text,
  ToastMessage,
  useToastMessage,
} from '@equip.health/ui';

import { useFlags } from 'launchdarkly-react-client-sdk';

import { ProfileEventsAdmin } from '~/analytics/profileEvents';
import { OnUpdateRolesParams } from '~/components/admin/users/PermissionsTree';
import {
  autoUpdatePermissionBasedOnRole,
  buildPermissionsTree,
  buildRolesAndPermissions,
  isAccessValid,
} from '~/lib/common/permissions';
import { ACCORDION_WIDTH_LARGE } from '~/lib/constants';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import {
  AdminRoles,
  MaudPermissions,
  MaudRoles,
  ProviderAccess,
  ProviderRole,
  OpsPermissions,
  OpsRoles,
  defaultAdminRolesState,
  defaultMaudRolesState,
  defaultProviderAccess,
  defaultOpsRolesState,
  providerDomainMap,
} from '~/lib/constants/permissions.constants';
import { UserStatus } from '~/lib/constants/user.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { UserMode, useModeContext } from '~/lib/contexts/ModeContext';
import {
  EditPermissionErrorsReducer,
  RolesAction,
  RolesReducer,
  errorsReducer,
  rolesReducer,
} from '~/store/reducers/userPermissions';
import { useUpdateProviderPermissionsMutation } from '~/store/services/providers';

import DiscardChangesModalV2 from '../DiscardChangesModalV2';
import PermissionsBox from '../PermissionsBox';
import { AppAccess } from '~/lib/constants/global.constants';
type PermissionData = {
  permissions: string[];
  providerExternalId: string;
  providerType: string;
  roles: string[];
};
interface EditPermissionAccordionProps {
  permissionData?: PermissionData;
  onDismissEditPermissionAccordion?: () => void;
  isExpanded: boolean;
  setIsExpanded: (expanded: boolean) => void;
}

const TOP_LEVEL_ID = 'edit-permissions-accordion';
const EditPermissionAccordion = ({
  permissionData,
  onDismissEditPermissionAccordion,
  isExpanded,
  setIsExpanded,
}: EditPermissionAccordionProps) => {
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] =
    useState<boolean>(false);
  const [isUnsavedPermissionChanges, setIsUnsavedPermissionChanges] =
    useState<boolean>(false);
  const [access, setAccess] = useState<AccessMap<ProviderAccess>>(
    defaultProviderAccess,
  );
  const hasMaudAccess = access[ProviderAccess.MAUD_ACCESS_APP];
  const hasAdminAccess = access[ProviderAccess.ADMIN_ACCESS_APP];
  const hasOpsAccess = access[ProviderAccess.OPS_ACCESS_APP];

  const hasAccess = useMemo<boolean>(
    () => hasMaudAccess || hasAdminAccess || hasOpsAccess,
    [hasMaudAccess, hasAdminAccess, hasOpsAccess],
  );
  const { mode } = useModeContext();
  const { scriptsureTechOps: isScriptsureTechOpsEnabled } = useFlags<LDFlags>();

  const [editProviderPermisisons, editProviderPermisisonsResult] =
    useUpdateProviderPermissionsMutation();

  const [maudRoles, dispatchMaudRoles] = useReducer<
    RolesReducer<MaudPermissions, MaudRoles>
  >(rolesReducer, defaultMaudRolesState);

  const [adminRoles, dispatchAdminRoles] = useReducer<
    RolesReducer<never, AdminRoles>
  >(rolesReducer, defaultAdminRolesState);

  const [opsRoles, dispatchOpsRoles] = useReducer<
    RolesReducer<OpsPermissions, OpsRoles>
  >(rolesReducer, defaultOpsRolesState);

  const isMaudAccessValid = useMemo<boolean>(
    () => isAccessValid(access, ProviderAccess.MAUD_ACCESS_APP, maudRoles),
    [access, maudRoles],
  );
  const isAdminAccessValid = useMemo<boolean>(
    () => isAccessValid(access, ProviderAccess.ADMIN_ACCESS_APP, adminRoles),
    [access, adminRoles],
  );
  const hasOpsPermission = Object.values(
    opsRoles[OpsRoles.OPS_PERMISSIONS].permissions,
  ).some(({ isChecked }: PermissionsState) => isChecked);
  const isOpsAccessValid =
    !isScriptsureTechOpsEnabled || !hasOpsAccess || hasOpsPermission;
  const isPermissionsValid = useMemo<boolean>(
    () => isMaudAccessValid && isAdminAccessValid && isOpsAccessValid,
    [isMaudAccessValid, isAdminAccessValid, isOpsAccessValid],
  );

  const dispatching = useRef<boolean>(false);

  const [errors, dispatchErrors] = useReducer<EditPermissionErrorsReducer>(
    errorsReducer(dispatching),
    {},
  );
  const { trackEvent } = useAnalytics();

  useEffect(() => {
    dispatchErrors({
      error: !isPermissionsValid,
      field: 'permissions',
    });
  }, [hasAccess, isPermissionsValid]);

  const handleRoleChange =
    <P extends string, R extends string>(
      dispatch: Dispatch<RolesAction<P, R>>,
    ) =>
    ({
      permission,
      role: userRole,
      value,
    }: OnUpdateRolesParams<P, R>): void => {
      if (
        userRole !== MaudRoles.MAUD_GENERAL_ACCESS &&
        userRole !== OpsRoles.OPS_GENERAL_ACCESS
      ) {
        setIsUnsavedPermissionChanges(true);
      }

      dispatch({ command: 'SET', data: { permission, role: userRole, value } });
    };

  useEffect(() => {
    handleRoleChange(dispatchMaudRoles)({
      role: MaudRoles.MAUD_GENERAL_ACCESS,
      value: hasMaudAccess,
    });
  }, [hasMaudAccess]);

  useEffect(() => {
    handleRoleChange(dispatchOpsRoles)({
      role: OpsRoles.OPS_GENERAL_ACCESS,
      value: hasOpsAccess,
    });
  }, [hasOpsAccess]);

  const { createToast } = useToastMessage();

  const buildUserRolesState = (
    userRoles: ProviderRole[],
    permissions: (MaudPermissions | OpsPermissions)[],
  ): void => {
    const updatedAdminRolesState = buildPermissionsTree<never, AdminRoles>(
      adminRoles,
      userRoles as AdminRoles[],
      [] as never[],
    );
    const updatedMaudRolesState = buildPermissionsTree<
      MaudPermissions,
      MaudRoles
    >(maudRoles, userRoles as MaudRoles[], permissions as MaudPermissions[]);
    const updatedOpsRolesState = buildPermissionsTree<OpsPermissions, OpsRoles>(
      opsRoles,
      userRoles as OpsRoles[],
      permissions as OpsPermissions[],
    );

    dispatchAdminRoles({ command: 'RESET', data: updatedAdminRolesState });
    dispatchMaudRoles({ command: 'RESET', data: updatedMaudRolesState });
    dispatchOpsRoles({
      command: 'RESET',
      data: updatedOpsRolesState,
    });
  };

  useEffect(() => {
    if (permissionData) {
      setAccess({
        [ProviderAccess.ADMIN_ACCESS_APP]:
          permissionData?.permissions?.includes(
            ProviderAccess.ADMIN_ACCESS_APP,
          ),
        [ProviderAccess.MAUD_ACCESS_APP]: permissionData?.permissions?.includes(
          ProviderAccess.MAUD_ACCESS_APP,
        ),
        [ProviderAccess.OPS_ACCESS_APP]: permissionData?.permissions?.includes(
          ProviderAccess.OPS_ACCESS_APP,
        ),
      });

      buildUserRolesState(
        permissionData?.roles as ProviderRole[],
        permissionData?.permissions as (MaudPermissions | OpsPermissions)[],
      );
      setIsUnsavedPermissionChanges(false);
    }
  }, [permissionData]);

  const onResetAndClose = (): void => {
    setIsUnsavedPermissionChanges(false);
    setIsUnsavedChangesModalOpen(false);
    onDismissEditPermissionAccordion();
  };

  const handleCancelAndClose = (): void => {
    if (isUnsavedPermissionChanges) {
      setIsUnsavedChangesModalOpen(true);
    } else {
      onResetAndClose();
    }
  };
  const adminAccessError = useMemo(() => {
    return hasAdminAccess && !adminRoles['admin:role:manage-users']?.isChecked
      ? 'To give admin access to this user, you must grant them at least one permission.'
      : null;
  }, [hasAdminAccess, adminRoles]);
  const opsAccessError = isOpsAccessValid
    ? null
    : 'To give Ops access to this user, you must grant them at least one permission.';

  const handleSave = (): void => {
    const role = permissionData?.providerType;
    const [userRoles, permissions] = buildRolesAndPermissions(
      access,
      providerDomainMap,
      {
        ...adminRoles,
        ...maudRoles,
        ...opsRoles,
      },
    );

    const autoUpdatedPermissions = autoUpdatePermissionBasedOnRole({
      hasMaudAccess,
      permissions,
      role,
    });

    const accountStatus = hasAccess ? UserStatus.ACTIVE : UserStatus.INACTIVE;
    if (mode === UserMode.ADMIN)
      trackEvent(ProfileEventsAdmin.permissionsSaveChangesButtonClicked, {
        source: AnalyticsSourceValues.PROFILE_TAB_ADMIN,
      });
    editProviderPermisisons({
      accountStatus,
      appAccess: [
        ...(hasAdminAccess ? [AppAccess.ADMIN] : []),
        ...(hasMaudAccess ? [AppAccess.MAUD] : []),
        ...(hasOpsAccess ? [AppAccess.OPS] : []),
      ],
      permissions: autoUpdatedPermissions,
      providerExternalId: permissionData?.providerExternalId,
      roles: userRoles,
    })
      .unwrap()
      .then(() => {
        setIsUnsavedChangesModalOpen(false);
        onDismissEditPermissionAccordion();
        createToast({
          icon: FAIconName.checkRegular,
          id: 'save-edit-permission_toast',
          text: 'User permissions updated',
          variant: 'success',
        });
      })
      .catch(() => {
        setIsUnsavedPermissionChanges(true);
      });
  };

  const handleAccessChange = (
    providerAccess: ProviderAccess,
    value: boolean,
  ): void => {
    if (mode === UserMode.ADMIN) {
      if (providerAccess === ProviderAccess.ADMIN_ACCESS_APP)
        trackEvent(ProfileEventsAdmin.permissionsAdminToggleButtonToggled, {
          source: AnalyticsSourceValues.PROFILE_TAB_ADMIN,
        });
      else if (providerAccess === ProviderAccess.MAUD_ACCESS_APP)
        trackEvent(ProfileEventsAdmin.permissionsMaudToggleButtonToggled, {
          source: AnalyticsSourceValues.PROFILE_TAB_ADMIN,
        });
    }

    setIsUnsavedPermissionChanges(true);
    setAccess((prev) => ({
      ...prev,
      [providerAccess]: value,
    }));
  };

  const footerContent = (
    <ChakraFlex alignItems="center" justifyContent="space-between" width="100%">
      <FireflyButton
        id={`${TOP_LEVEL_ID}__cancel-button`}
        leftIcon={FAIconName.XmarkRegular}
        onClick={handleCancelAndClose}
        variant="secondary"
      >
        Cancel
      </FireflyButton>
      <FireflyButton
        id={`${TOP_LEVEL_ID}__save-button`}
        isDisabled={!isUnsavedPermissionChanges || !!errors.permissions}
        isLoading={editProviderPermisisonsResult?.isLoading}
        leftIcon={FAIconName.checkRegular}
        onClick={handleSave}
      >
        Save changes
      </FireflyButton>
    </ChakraFlex>
  );
  return (
    <>
      <DiscardChangesModalV2
        isOpen={isUnsavedChangesModalOpen}
        onClose={() => setIsUnsavedChangesModalOpen(false)}
        onConfirm={onResetAndClose}
      />
      <Accordion
        dismissMenu={handleCancelAndClose}
        footer={footerContent}
        id={TOP_LEVEL_ID}
        isExpanded={isExpanded}
        panelMaxWidth={ACCORDION_WIDTH_LARGE}
        panelMinWidth={ACCORDION_WIDTH_LARGE}
        setIsExpanded={setIsExpanded}
        title="Edit permissions"
      >
        <ChakraFormControl isInvalid={!!errors.permissions} padding="24px">
          <ChakraSimpleGrid
            columns={isScriptsureTechOpsEnabled ? 3 : 2}
            marginBottom="10px"
            spacing="16px"
          >
            <PermissionsBox<MaudPermissions, MaudRoles>
              access={ProviderAccess.MAUD_ACCESS_APP}
              caption="User will have access to all the features available on Equip MAUD."
              isAccessChecked={access[ProviderAccess.MAUD_ACCESS_APP]}
              isInvalid={!!errors.permissions}
              label="MAUD"
              onUpdateAccess={handleAccessChange}
              onUpdateRoles={handleRoleChange(dispatchMaudRoles)}
              roles={maudRoles}
            />
            <PermissionsBox<never, AdminRoles>
              access={ProviderAccess.ADMIN_ACCESS_APP}
              caption="Allow user to navigate to Admin panel from Equip.\nYou can define different access to each feature."
              isAccessChecked={access[ProviderAccess.ADMIN_ACCESS_APP]}
              isInvalid={!!errors.permissions}
              label="Admin"
              onUpdateAccess={handleAccessChange}
              onUpdateRoles={handleRoleChange(dispatchAdminRoles)}
              roles={adminRoles}
            />
            {isScriptsureTechOpsEnabled && (
              <PermissionsBox<OpsPermissions, OpsRoles>
                access={ProviderAccess.OPS_ACCESS_APP}
                caption="Allow user to navigate to the Ops platform from Equip."
                isAccessChecked={hasOpsAccess}
                isInvalid={!!errors.permissions}
                label="Ops"
                onUpdateAccess={handleAccessChange}
                onUpdateRoles={handleRoleChange(dispatchOpsRoles)}
                roles={opsRoles}
              />
            )}
          </ChakraSimpleGrid>

          {!hasAccess && (
            <Text
              color="critical.base"
              marginBottom="10px"
              marginTop="10px"
              variant="caption"
            >
              {isScriptsureTechOpsEnabled
                ? 'By toggling off Admin, MAUD, and Ops, this user will no longer be able to log into any Equip app.'
                : 'By toggling off Admin and MAUD, this user will no longer be able to log into any Equip app.'}
            </Text>
          )}

          {adminAccessError && (
            <Text
              color="critical.base"
              marginBottom="10px"
              marginTop="10px"
              variant="caption"
            >
              {adminAccessError}
            </Text>
          )}

          {opsAccessError && (
            <Text
              color="critical.base"
              marginBottom="10px"
              marginTop="10px"
              variant="caption"
            >
              {opsAccessError}
            </Text>
          )}

          {editProviderPermisisonsResult.isError && (
            <ToastMessage
              icon={FAIconName.exclamationTriangleRegular}
              id={`${TOP_LEVEL_ID}__warning`}
              isFullWidth
              text="We're sorry, we were unable to save your changes. Please try
                  again."
              variant="critical"
            />
          )}
        </ChakraFormControl>
      </Accordion>
    </>
  );
};

export default EditPermissionAccordion;
